import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './core/services/AuthStore';
import ServiceContainer from './core/services/ServiceContainer';
import { Sidebar } from './shared/components/Sidebar';
import { LoginPage } from './modules/auth/pages/LoginPage';
import { ForgotPasswordPage } from './modules/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from './modules/auth/pages/ResetPasswordPage';
import { AcceptInvitePage } from './modules/auth/pages/AcceptInvitePage';
import { DashboardPage } from './modules/messages/pages/DashboardPage';
import { UsersPage } from './modules/users/pages/UsersPage';
import { ClientsPage } from './modules/clients/pages/ClientsPage';
import { DamsPage } from './modules/dams/pages/DamsPage';
import { ContactsPage } from './modules/contacts/pages/ContactsPage';
import { GroupsPage } from './modules/groups/pages/GroupsPage';
import { SendMessagesPage } from './modules/messages/pages/SendMessagesPage';
import { TemplatesPage } from './modules/templates/pages/TemplatesPage';
import { LogsPage } from './modules/logs/pages/LogsPage';
import { UnauthorizedPage } from './shared/pages/UnauthorizedPage';
import { GlobalLoadingBar } from './shared/components/GlobalLoadingBar';
import './App.css';

// Layout principal com sidebar estática
function MainLayout() {
  const { token, user } = useAuthStore();

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user } = useAuthStore();

  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles || [];
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

function App() {
  const { token, user, setToken } = useAuthStore();
  const apiClient = ServiceContainer.getInstance().getApiClient();

  useEffect(() => {
    // Restaurar token do localStorage ao inicializar
    const savedToken = localStorage.getItem('authToken');
    if (savedToken) {
      setToken(savedToken);
      apiClient.setAuthToken(savedToken);
    }
  }, [setToken, apiClient]);

  useEffect(() => {
    if (!token || !user) {
      return;
    }

    // Prefetch das telas mais usadas para deixar a navegação mais fluida.
    const prefetchConfig = {
      headers: {
        'x-skip-loading': true,
        'x-cache-ttl-ms': 60000,
      },
    };

    void Promise.allSettled([
      apiClient.get('/groups', prefetchConfig),
      apiClient.get('/dams', prefetchConfig),
      apiClient.get('/message-templates', prefetchConfig),
      apiClient.get('/contacts', prefetchConfig),
    ]);
  }, [token, user, apiClient]);

  return (
    <Router>
      <GlobalLoadingBar />
      <Routes>
        {/* Rotas públicas sem sidebar */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Rotas protegidas com sidebar estática */}
        <Route element={<MainLayout />}>
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredRoles={['admin', 'gestor']}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          
          <Route 
            path="/users" 
            element={
              <ProtectedRoute requiredRoles={['admin', 'gestor']}>
                <UsersPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/clients" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <ClientsPage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/dams" 
            element={
              <ProtectedRoute requiredRoles={['admin', 'gestor', 'colaborador']}>
                <DamsPage />
              </ProtectedRoute>
            } 
          />
          
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/send-messages" element={<SendMessagesPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          
          <Route 
            path="/logs" 
            element={
              <ProtectedRoute requiredRoles={['admin', 'gestor']}>
                <LogsPage />
              </ProtectedRoute>
            } 
          />
        </Route>

        {/* Redirects */}
        <Route
          path="/"
          element={
            token
              ? <Navigate to={user?.roles?.includes('colaborador') ? '/send-messages' : '/dashboard'} />
              : <Navigate to="/login" />
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
